import React, { useState, useEffect, useContext } from "react";
import { toast } from "react-toastify";
import moment from "moment";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip as RTooltip, ResponsiveContainer, Cell,
} from "recharts";

import {
  AppBar, Toolbar, Box, Button, Card, CardContent, Chip, CircularProgress,
  Grid, MenuItem, Stack, Tab, Tabs, Table, TableBody, TableCell, TableHead,
  TableRow, TextField, Typography, alpha,
} from "@mui/material";
import { Logout, Groups, Add, Store, Payments, Sell, ViewKanban, RequestQuote } from "@mui/icons-material";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";

const brl = (v) => Number(v || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
// Comissões com 3 casas decimais
const brl3 = (v) => "R$ " + Number(v || 0).toLocaleString("pt-BR", { minimumFractionDigits: 3, maximumFractionDigits: 3 });

const STAGES = [
  { key: "novo", label: "Novo", color: "#1565c0" },
  { key: "ativo", label: "Ativo", color: "#2e7d32" },
  { key: "inativo", label: "Inativo", color: "#c62828" },
];

const PAYOUT_STATUS = {
  pending: { label: "Aguardando", color: "#f9a825", bg: "#fff8e1" },
  paid: { label: "Pago", color: "#2e7d32", bg: "#e8f5e9" },
  rejected: { label: "Rejeitado", color: "#c62828", bg: "#ffebee" },
};

const AffiliatePanel = () => {
  const { user, handleLogout } = useContext(AuthContext);

  const [tab, setTab] = useState(0);
  const [plans, setPlans] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [commissions, setCommissions] = useState({ commissions: [], totalPending: 0, totalPaid: 0 });
  const [payouts, setPayouts] = useState([]);
  const [loading, setLoading] = useState(true);

  const [form, setForm] = useState({ name: "", email: "", password: "", phone: "", document: "", planId: "" });
  const [saving, setSaving] = useState(false);
  const [requesting, setRequesting] = useState(false);
  const [dragId, setDragId] = useState(null);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [p, c, com, pay] = await Promise.all([
        api.get("/affiliate-panel/plans"),
        api.get("/affiliate-panel/companies"),
        api.get("/affiliate-panel/commissions"),
        api.get("/affiliate-panel/payouts"),
      ]);
      setPlans(p.data || []);
      setCompanies(c.data || []);
      setCommissions(com.data || { commissions: [], totalPending: 0, totalPaid: 0 });
      setPayouts(pay.data || []);
    } catch (err) {
      toastError(err);
    }
    setLoading(false);
  };

  useEffect(() => { loadAll(); /* eslint-disable-next-line */ }, []);

  const handleCreateCompany = async () => {
    if (!form.name || !form.email || !form.planId) return toast.warn("Preencha nome, email e plano.");
    if (!form.password || form.password.length < 5) return toast.warn("Defina uma senha de acesso (mín. 5 caracteres).");
    setSaving(true);
    try {
      await api.post("/affiliate-panel/companies", form);
      toast.success("Empresa cadastrada com sucesso!");
      setForm({ name: "", email: "", password: "", phone: "", document: "", planId: "" });
      await loadAll();
      setTab(2);
    } catch (err) { toastError(err); }
    setSaving(false);
  };

  const handleRequestPayout = async () => {
    if (commissions.totalPending <= 0) return toast.warn("Você não tem comissões disponíveis para saque.");
    if (!window.confirm(`Solicitar o pagamento de ${brl3(commissions.totalPending)}?`)) return;
    setRequesting(true);
    try {
      await api.post("/affiliate-panel/payout-request");
      toast.success("Solicitação de pagamento enviada!");
      await loadAll();
    } catch (err) { toastError(err); }
    setRequesting(false);
  };

  const moveStage = async (companyId, stage) => {
    setCompanies((prev) => prev.map((c) => (c.id === companyId ? { ...c, kanbanStage: stage } : c)));
    try {
      await api.put(`/affiliate-panel/companies/${companyId}/stage`, { stage });
    } catch (err) { toastError(err); loadAll(); }
  };

  const commissionLabel = (p) => (p.commissionType === "percent" ? `${p.commissionValue}%` : brl3(p.commissionValue));
  const isNew = (createdAt) => moment().diff(moment(createdAt), "days") <= 7;

  const chartData = [
    { name: "A receber", valor: Number(commissions.totalPending || 0), fill: "#f9a825" },
    { name: "Já recebido", valor: Number(commissions.totalPaid || 0), fill: "#2e7d32" },
  ];

  return (
    <Box sx={{ minHeight: "100vh", background: (t) => t.palette.background.default }}>
      <AppBar position="static" elevation={0} sx={{ background: "#5852ab" }}>
        <Toolbar>
          <Groups sx={{ mr: 1.5 }} />
          <Typography variant="h6" sx={{ fontWeight: 700, flexGrow: 1 }}>Painel do Afiliado</Typography>
          <Typography variant="body2" sx={{ mr: 2, opacity: 0.9 }}>{user?.name}</Typography>
          <Button color="inherit" startIcon={<Logout />} onClick={handleLogout} sx={{ textTransform: "none" }}>Sair</Button>
        </Toolbar>
      </AppBar>

      <Box sx={{ maxWidth: 1100, mx: "auto", p: { xs: 1.5, md: 3 } }}>
        {/* Resumo + gráfico */}
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={12} sm={4}>
            <Card elevation={0} sx={{ borderRadius: 3, border: "1.5px solid", borderColor: "divider", height: "100%" }}>
              <CardContent>
                <Stack direction="row" spacing={1.5} alignItems="center">
                  <Box sx={{ width: 44, height: 44, borderRadius: 2, background: alpha("#f9a825", 0.15), display: "flex", alignItems: "center", justifyContent: "center" }}><Payments sx={{ color: "#f9a825" }} /></Box>
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 800, color: "#f9a825" }}>{brl3(commissions.totalPending)}</Typography>
                    <Typography variant="caption" color="text.secondary">Comissões a receber</Typography>
                  </Box>
                </Stack>
                <Button fullWidth size="small" variant="contained" startIcon={<RequestQuote />} onClick={handleRequestPayout}
                  disabled={requesting || commissions.totalPending <= 0}
                  sx={{ mt: 1.5, borderRadius: 2, textTransform: "none", fontWeight: 600, background: "#f9a825", "&:hover": { background: "#e69512" } }}>
                  {requesting ? <CircularProgress size={18} sx={{ color: "#fff" }} /> : "Solicitar pagamento"}
                </Button>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Card elevation={0} sx={{ borderRadius: 3, border: "1.5px solid", borderColor: "divider", height: "100%" }}>
              <CardContent>
                <Stack direction="row" spacing={1.5} alignItems="center">
                  <Box sx={{ width: 44, height: 44, borderRadius: 2, background: alpha("#2e7d32", 0.15), display: "flex", alignItems: "center", justifyContent: "center" }}><Payments sx={{ color: "#2e7d32" }} /></Box>
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 800, color: "#2e7d32" }}>{brl3(commissions.totalPaid)}</Typography>
                    <Typography variant="caption" color="text.secondary">Comissões pagas</Typography>
                  </Box>
                </Stack>
                <Box sx={{ height: 70, mt: 1 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={chartData} margin={{ top: 5, right: 5, left: 5, bottom: 0 }}>
                      <XAxis dataKey="name" tick={{ fontSize: 10 }} />
                      <YAxis hide />
                      <RTooltip formatter={(v) => brl3(v)} />
                      <Bar dataKey="valor" radius={[4, 4, 0, 0]}>
                        {chartData.map((e, i) => <Cell key={i} fill={e.fill} />)}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Card elevation={0} sx={{ borderRadius: 3, border: "1.5px solid", borderColor: "divider", height: "100%" }}>
              <CardContent>
                <Stack direction="row" spacing={1.5} alignItems="center">
                  <Box sx={{ width: 44, height: 44, borderRadius: 2, background: alpha("#1565c0", 0.15), display: "flex", alignItems: "center", justifyContent: "center" }}><Store sx={{ color: "#1565c0" }} /></Box>
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 800, color: "#1565c0" }}>{companies.length}</Typography>
                    <Typography variant="caption" color="text.secondary">Empresas cadastradas</Typography>
                  </Box>
                </Stack>
                <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1.5 }}>
                  {companies.filter((c) => isNew(c.createdAt)).length} nova(s) nos últimos 7 dias · {companies.filter((c) => c.kanbanStage === "ativo").length} ativa(s)
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        <Card elevation={0} sx={{ borderRadius: 3, border: "1.5px solid", borderColor: "divider" }}>
          <Tabs value={tab} onChange={(e, v) => setTab(v)} variant="scrollable" sx={{ borderBottom: 1, borderColor: "divider", px: 1 }}>
            <Tab icon={<Sell fontSize="small" />} iconPosition="start" label="Planos" sx={{ textTransform: "none", fontWeight: 600, minHeight: 48 }} />
            <Tab icon={<Add fontSize="small" />} iconPosition="start" label="Cadastrar Empresa" sx={{ textTransform: "none", fontWeight: 600, minHeight: 48 }} />
            <Tab icon={<Store fontSize="small" />} iconPosition="start" label="Minhas Empresas" sx={{ textTransform: "none", fontWeight: 600, minHeight: 48 }} />
            <Tab icon={<ViewKanban fontSize="small" />} iconPosition="start" label="Kanban" sx={{ textTransform: "none", fontWeight: 600, minHeight: 48 }} />
            <Tab icon={<Payments fontSize="small" />} iconPosition="start" label="Comissões" sx={{ textTransform: "none", fontWeight: 600, minHeight: 48 }} />
          </Tabs>

          <Box sx={{ p: { xs: 2, md: 3 } }}>
            {loading ? (
              <Box sx={{ textAlign: "center", py: 5 }}><CircularProgress /></Box>
            ) : (
              <>
                {/* Planos */}
                {tab === 0 && (
                  <Grid container spacing={2}>
                    {plans.length === 0 && <Grid item xs={12}><Typography color="text.secondary" align="center" sx={{ py: 3 }}>Nenhum plano liberado para você ainda. Fale com o administrador.</Typography></Grid>}
                    {plans.map((p) => (
                      <Grid item xs={12} sm={6} md={4} key={p.planId}>
                        <Card elevation={0} sx={{ borderRadius: 3, border: "1.5px solid", borderColor: "divider", height: "100%" }}>
                          <CardContent>
                            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{p.planName}</Typography>
                            <Typography variant="h6" sx={{ fontWeight: 800, color: "#5852ab", my: 0.5 }}>{brl(p.planAmount)}</Typography>
                            <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                              <Chip size="small" label={`Comissão: ${commissionLabel(p)}`} sx={{ background: alpha("#2e7d32", 0.15), color: "#2e7d32", fontWeight: 600 }} />
                              <Chip size="small" label={p.recurrence === "recurring" ? "Recorrente" : "Única"} sx={{ background: alpha("#1565c0", 0.15), color: "#1565c0", fontWeight: 600 }} />
                            </Stack>
                          </CardContent>
                        </Card>
                      </Grid>
                    ))}
                  </Grid>
                )}

                {/* Cadastrar Empresa */}
                {tab === 1 && (
                  <Box sx={{ maxWidth: 560 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      A empresa é criada já ativa e vinculada a você. Informe o email e a senha de acesso que a empresa vai usar para entrar no sistema.
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}><TextField label="Nome da empresa" size="small" fullWidth value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></Grid>
                      <Grid item xs={12} sm={6}><TextField label="Email (login da empresa)" size="small" fullWidth value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></Grid>
                      <Grid item xs={12} sm={6}><TextField label="Senha de acesso" type="password" size="small" fullWidth value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} /></Grid>
                      <Grid item xs={12} sm={6}><TextField label="Telefone" size="small" fullWidth value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></Grid>
                      <Grid item xs={12} sm={6}><TextField label="Documento (CPF/CNPJ)" size="small" fullWidth value={form.document} onChange={(e) => setForm({ ...form, document: e.target.value })} /></Grid>
                      <Grid item xs={12}>
                        <TextField select label="Plano" size="small" fullWidth value={form.planId} onChange={(e) => setForm({ ...form, planId: e.target.value })}>
                          {plans.map((p) => (<MenuItem key={p.planId} value={p.planId}>{p.planName} — {brl(p.planAmount)}</MenuItem>))}
                        </TextField>
                      </Grid>
                      <Grid item xs={12}>
                        <Button variant="contained" onClick={handleCreateCompany} disabled={saving} sx={{ textTransform: "none", fontWeight: 600, borderRadius: 2, background: "#5852ab" }}>
                          {saving ? <CircularProgress size={20} sx={{ color: "#fff" }} /> : "Cadastrar Empresa"}
                        </Button>
                      </Grid>
                    </Grid>
                  </Box>
                )}

                {/* Minhas Empresas */}
                {tab === 2 && (
                  <Box sx={{ overflowX: "auto" }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 700 }}>Empresa</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>Plano</TableCell>
                          <TableCell sx={{ fontWeight: 700 }} align="center">Cadastrada em</TableCell>
                          <TableCell sx={{ fontWeight: 700 }} align="center">Situação</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {companies.map((c) => (
                          <TableRow key={c.id} hover>
                            <TableCell>
                              {c.name}
                              {isNew(c.createdAt) && <Chip size="small" label="Nova" sx={{ ml: 1, height: 18, fontSize: "0.65rem", background: alpha("#1565c0", 0.15), color: "#1565c0", fontWeight: 700 }} />}
                            </TableCell>
                            <TableCell>{c.planName}</TableCell>
                            <TableCell align="center">{moment(c.createdAt).format("DD/MM/YYYY")}</TableCell>
                            <TableCell align="center">
                              <Chip size="small" label={c.status ? "Ativa" : "Inativa"} sx={{ background: c.status ? alpha("#2e7d32", 0.15) : alpha("#757575", 0.15), color: c.status ? "#2e7d32" : "#757575", fontWeight: 600 }} />
                            </TableCell>
                          </TableRow>
                        ))}
                        {companies.length === 0 && <TableRow><TableCell colSpan={4} align="center" sx={{ py: 3, color: "text.secondary" }}>Você ainda não cadastrou empresas.</TableCell></TableRow>}
                      </TableBody>
                    </Table>
                  </Box>
                )}

                {/* Kanban */}
                {tab === 3 && (
                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 2 }}>
                      Arraste os clientes entre as colunas para organizar o acompanhamento.
                    </Typography>
                    <Grid container spacing={2}>
                      {STAGES.map((st) => (
                        <Grid item xs={12} md={4} key={st.key}>
                          <Box
                            onDragOver={(e) => e.preventDefault()}
                            onDrop={() => { if (dragId) { moveStage(dragId, st.key); setDragId(null); } }}
                            sx={{ background: alpha(st.color, 0.06), border: `1.5px solid ${alpha(st.color, 0.3)}`, borderRadius: 3, p: 1.5, minHeight: 300 }}
                          >
                            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.5, px: 0.5 }}>
                              <Typography sx={{ fontWeight: 700, color: st.color }}>{st.label}</Typography>
                              <Chip size="small" label={companies.filter((c) => (c.kanbanStage || "novo") === st.key).length} sx={{ background: alpha(st.color, 0.15), color: st.color, fontWeight: 700 }} />
                            </Stack>
                            <Stack spacing={1}>
                              {companies.filter((c) => (c.kanbanStage || "novo") === st.key).map((c) => (
                                <Card key={c.id} elevation={0} draggable
                                  onDragStart={() => setDragId(c.id)}
                                  sx={{ p: 1.25, borderRadius: 2, border: "1px solid", borderColor: "divider", cursor: "grab", background: (t) => t.palette.background.paper }}>
                                  <Typography variant="body2" sx={{ fontWeight: 600 }}>{c.name}</Typography>
                                  <Typography variant="caption" color="text.secondary">{c.planName}</Typography>
                                </Card>
                              ))}
                              {companies.filter((c) => (c.kanbanStage || "novo") === st.key).length === 0 && (
                                <Typography variant="caption" color="text.secondary" sx={{ px: 0.5 }}>Nenhum cliente aqui.</Typography>
                              )}
                            </Stack>
                          </Box>
                        </Grid>
                      ))}
                    </Grid>
                  </Box>
                )}

                {/* Comissões + Saques */}
                {tab === 4 && (
                  <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>Comissões</Typography>
                    <Box sx={{ overflowX: "auto", mb: 3 }}>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell sx={{ fontWeight: 700 }}>Empresa</TableCell>
                            <TableCell sx={{ fontWeight: 700 }}>Tipo</TableCell>
                            <TableCell sx={{ fontWeight: 700 }} align="right">Valor</TableCell>
                            <TableCell sx={{ fontWeight: 700 }} align="center">Status</TableCell>
                            <TableCell sx={{ fontWeight: 700 }} align="center">Data</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {commissions.commissions.map((c) => (
                            <TableRow key={c.id} hover>
                              <TableCell>{c.companyName}</TableCell>
                              <TableCell>{c.type === "implementation" ? "Implantação" : "Recorrente"}</TableCell>
                              <TableCell align="right">{brl3(c.value)}</TableCell>
                              <TableCell align="center">
                                <Chip size="small" label={c.status === "paid" ? "Paga" : "A receber"} sx={{ background: c.status === "paid" ? alpha("#2e7d32", 0.15) : alpha("#f9a825", 0.15), color: c.status === "paid" ? "#2e7d32" : "#854f0b", fontWeight: 600 }} />
                              </TableCell>
                              <TableCell align="center">{moment(c.createdAt).format("DD/MM/YYYY")}</TableCell>
                            </TableRow>
                          ))}
                          {commissions.commissions.length === 0 && <TableRow><TableCell colSpan={5} align="center" sx={{ py: 3, color: "text.secondary" }}>Nenhuma comissão ainda.</TableCell></TableRow>}
                        </TableBody>
                      </Table>
                    </Box>

                    <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>Solicitações de pagamento</Typography>
                    <Box sx={{ overflowX: "auto" }}>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell sx={{ fontWeight: 700 }}>Data</TableCell>
                            <TableCell sx={{ fontWeight: 700 }} align="right">Valor</TableCell>
                            <TableCell sx={{ fontWeight: 700 }} align="center">Status</TableCell>
                            <TableCell sx={{ fontWeight: 700 }} align="center">Pago em</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {payouts.map((p) => {
                            const st = PAYOUT_STATUS[p.status] || PAYOUT_STATUS.pending;
                            return (
                              <TableRow key={p.id} hover>
                                <TableCell>{moment(p.createdAt).format("DD/MM/YYYY")}</TableCell>
                                <TableCell align="right">{brl3(p.amount)}</TableCell>
                                <TableCell align="center"><Chip size="small" label={st.label} sx={{ background: st.bg, color: st.color, fontWeight: 600 }} /></TableCell>
                                <TableCell align="center">{p.paidAt ? moment(p.paidAt).format("DD/MM/YYYY") : "-"}</TableCell>
                              </TableRow>
                            );
                          })}
                          {payouts.length === 0 && <TableRow><TableCell colSpan={4} align="center" sx={{ py: 3, color: "text.secondary" }}>Nenhuma solicitação ainda.</TableCell></TableRow>}
                        </TableBody>
                      </Table>
                    </Box>
                  </Box>
                )}
              </>
            )}
          </Box>
        </Card>
      </Box>
    </Box>
  );
};

export default AffiliatePanel;
